import { type Input } from "antd";
import { debounce } from "lodash-es";
import {
    type Dispatch,
    type MutableRefObject,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useHistory, useRouteMatch } from "react-router";
import { Key } from "ts-key-enum";

import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";

import { type IAPIHandlerResult } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";
import { BTContentRoot } from "types/constants";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { NetworkError } from "utilities/apiHandler";
import { useKeyPress } from "utilities/keyboard/keyboard";
import { scrollToElement } from "utilities/scroll/scroll";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import {
    BTIconArrowLeftOutlined,
    BTIconExclamationCircleOutlinedError,
    BTIconLoadingOutlined,
    BTIconMagnifyingGlassX,
    BTIconSearch,
} from "commonComponents/btWrappers/BTIcon";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { emptyStateIconSize } from "commonComponents/entity/emptyState/common/EmptyState.api.types";
import { EmptyState } from "commonComponents/entity/emptyState/EmptyState";
import { AutoSearchInput } from "commonComponents/utilities/Input/AutoSearchInput";
import {
    type ISearchBarHandler,
    SearchBarHandler,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.handler";
import {
    getResultKeyIndex,
    getSearchResultKey,
    type ISearchBarResult,
    type SearchBarEntity,
    type SearchCategory,
    SearchInteractiveItems,
    SearchResultScreen,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";
import {
    getSearchBarResultsWithHighlights,
    handleSearchError,
    LinkType,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.utilities";
import { SearchResultCard } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchResultCard";

import "./SearchBar.less";

export interface IOuterSearchBarProps extends IEmptyScreenProps {
    isCollapsed?: boolean;
    handler?: ISearchBarHandler;
    searchLimit: number;
    presetSearchCategory?: SearchCategory[];
    shouldAutoFillOnHover?: boolean;
    onValueChange?: (value: string) => void;
    onResultItemClick?: (result: ISearchBarResult) => Promise<string | void>;
    onResultItemHover?: (result?: ISearchBarResult) => void;
    getCloseOnScrollTargets?: (el?: HTMLElement) => HTMLElement[] | null;
    value?: string;
    className?: string;
    inputPadding?: boolean;
    /**
     * Owned by the caller because `commonComponents` may not read builder flags
     * directly. Pass `useContext(BuilderInfoContext)` from the parent. Used to
     * flag-gate entity result links (bills, warranties) to navigate to a page
     * route instead of the relative modal route.
     */
    builderInfo?: BuilderInfo | null;
    /**
     * Focus the input as soon as this instance mounts - for a brand-new, blank line item row
     * where the title search should be ready to type into immediately. Unrelated to the
     * interaction-driven auto-focus (autoFocusSearchBar) used when the collapsed nav search bar
     * expands or a result is selected with shouldAutoFillOnHover.
     */
    autoFocus?: boolean;
}

interface ISearchBarProps extends IOuterSearchBarProps {
    isCollapsed: boolean;
    onSearchToggle: () => void;
}

const defaultSearchHandler = new SearchBarHandler();

const noOpt = () => {};

export const SearchBar: React.FunctionComponent<ISearchBarProps & ITrackingProp> = track({
    component: "Search Bar",
})(({
    isCollapsed,
    onSearchToggle,
    handler = defaultSearchHandler,
    searchLimit,
    tracking,
    presetSearchCategory,
    onResultItemClick,
    shouldAutoFillOnHover,
    onValueChange,
    onResultItemHover,
    getCloseOnScrollTargets,
    inputPadding = true,
    builderInfo,
    autoFocus,
    ...props
}) => {
    // #region State
    const [isLoading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isListOpen, setIsListOpen] = useState(false);
    const [searchData, setSearchData] = useState<SearchBarEntity | undefined>();
    const [keyboardOrder, setKeyboardOrder] = useState<(SearchInteractiveItems | string)[]>([]);
    const [activeItemIndex, setActiveItemIndexState] = useState(0);
    const [errorState, setErrorState] = useState<string | undefined>();
    // #endregion

    // #region Constants/Variables
    const searchResults = searchData?.processedResults;
    const activeKeyboardItem = keyboardOrder[activeItemIndex];

    const searchApiHandlerResult = useRef<IAPIHandlerResult<SearchBarEntity> | undefined>();
    const inputRef = useRef<Input | null>(null);
    const autoFocused = useRef<boolean>(false);
    const searchTimestamp = useRef(0);
    const resultsRef = useRef<HTMLDivElement | null>(null);

    const history = useHistory();
    const match = useRouteMatch();

    const previousActiveIndex = useRef(-1);
    const onActiveItemHover = useCallback(
        (index: number) => {
            if (
                !shouldAutoFillOnHover ||
                !onResultItemHover ||
                previousActiveIndex.current === index
            ) {
                return;
            }
            previousActiveIndex.current = index;
            let result: ISearchBarResult | undefined = undefined;

            if (keyboardOrder.length > index) {
                const hoveredIndexInfo = keyboardOrder[index];
                if (hoveredIndexInfo.includes(SearchInteractiveItems.SearchResults)) {
                    result = searchResults
                        ? searchResults[getResultKeyIndex(hoveredIndexInfo)]
                        : undefined;
                }
                onResultItemHover(result);
            }
        },
        [keyboardOrder, onResultItemHover, searchResults, shouldAutoFillOnHover],
    );

    const onHoverOutside = useCallback(
        (e: MouseEvent) => {
            if (!shouldAutoFillOnHover || !onResultItemHover) {
                return;
            }
            if (
                !(e.target as HTMLElement | null)?.matches(".SearchResults *") &&
                previousActiveIndex.current !== -1
            ) {
                previousActiveIndex.current = -1;
                onResultItemHover(undefined);
            }
        },
        [onResultItemHover, shouldAutoFillOnHover],
    );

    const setActiveItemIndex = useCallback(
        (action: SetStateAction<number>) => {
            if (typeof action === "function") {
                setActiveItemIndexState((prevState) => {
                    const newState = action(prevState);
                    onActiveItemHover(newState);
                    return newState;
                });
            } else {
                setActiveItemIndexState(action);
                onActiveItemHover(action);
            }
        },
        [onActiveItemHover],
    );

    const screenToShow = useMemo(() => {
        if (errorState !== undefined) {
            return SearchResultScreen.Error;
        } else if (
            searchData !== undefined &&
            searchData.processedResults.length === 0 &&
            !shouldAutoFillOnHover
        ) {
            return SearchResultScreen.Empty;
        } else if (searchData !== undefined) {
            return SearchResultScreen.Results;
        } else {
            return SearchResultScreen.Closed;
        }
    }, [errorState, searchData, shouldAutoFillOnHover]);
    // #endregion

    const autoFocusSearchBar = useCallback(() => {
        if (inputRef.current) {
            autoFocused.current = true;
            requestAnimationFrame(() => inputRef.current!.input?.focus());
        }
    }, []);

    // Focus on mount for a brand-new row's title search - a one-time mount effect rather than
    // reacting to the autoFocus prop, since this instance is unmounted/replaced (not toggled)
    // once the row stops being new.
    useEffect(() => {
        if (autoFocus) {
            autoFocusSearchBar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const suffix = useMemo(() => {
        if (isLoading) {
            return <BTIconLoadingOutlined size={12} />;
        } else {
            return <div className="EmptyIcon" />;
        }
    }, [isLoading]);

    const resetState = useCallback(
        (unfocusInput = true) => {
            setIsListOpen(false);
            setSearchData(undefined);
            setErrorState(undefined);
            setActiveItemIndex(0);
            onResultItemHover?.(undefined);

            if (!shouldAutoFillOnHover) {
                autoFocused.current = false;
            }

            if (inputRef.current?.input.value === "" && shouldAutoFillOnHover) {
                autoFocusSearchBar();
            }

            onSearchToggle();

            if (unfocusInput) {
                requestAnimationFrame(() => inputRef.current?.input?.blur());
            }
        },
        [
            onSearchToggle,
            shouldAutoFillOnHover,
            autoFocusSearchBar,
            setActiveItemIndex,
            onResultItemHover,
        ],
    );

    // #region SearchBar
    const handleCancelPreviousApiRequest = useCallback(() => {
        if (searchApiHandlerResult.current) {
            searchApiHandlerResult.current.cancel();
            searchApiHandlerResult.current = undefined;
        }
    }, []);

    const shouldAbortSearch = useCallback(
        (userInput: string) => {
            // Don't search if the input is blank or if the search bar is closed. Can't rely on state since it isn't always up to date.
            const isBlank = userInput.trim().length === 0;
            const isUiClosed =
                !shouldAutoFillOnHover &&
                !inputRef.current?.input.parentElement?.classList.contains("SearchBarInputOpen");

            return isBlank || isUiClosed;
        },
        [shouldAutoFillOnHover],
    );

    const handleSearch = useCallback(
        async (value: string) => {
            if (shouldAbortSearch(value)) {
                return;
            }

            // An existing request should be cancelled when a new one is made
            handleCancelPreviousApiRequest();

            try {
                setErrorState(undefined);
                setLoading(true);

                searchTimestamp.current = performance.now();
                searchApiHandlerResult.current = handler.get({
                    search: value,
                    categories: presetSearchCategory ?? [],
                    limit: searchLimit,
                });

                const resp = await searchApiHandlerResult.current.response;
                if (resp) {
                    resp.processedResults = getSearchBarResultsWithHighlights(
                        resp?.results,
                        undefined,
                        onResultItemClick !== undefined,
                        builderInfo ?? undefined,
                    );
                }
                const durationMs = performance.now() - searchTimestamp.current;

                // Check again to avoid race conditions
                if (shouldAbortSearch(value)) {
                    setLoading(false);
                    return;
                }

                if (inputRef.current?.input !== document.activeElement) {
                    setLoading(false);
                    return;
                }

                const nresp: SearchBarEntity | undefined =
                    resp !== undefined ? { ...resp, ...{ searchTerm: value } } : undefined;

                setSearchData(nresp);
                setActiveItemIndex(0);
                setLoading(false);

                tracking?.trackEvent({
                    event: "Search",
                    extraInfo: {
                        duration: `${durationMs} ms`,
                        query: value,
                        ...(resp?.debug ?? {}),
                    },
                });
            } catch (e) {
                // Display the error state
                setErrorState(handleSearchError(e));

                if (!(e instanceof NetworkError)) {
                    // Reset everything
                    setSearchData(undefined);
                    setActiveItemIndex(0);
                    setLoading(false);
                }
            }
        },
        [
            shouldAbortSearch,
            handleCancelPreviousApiRequest,
            handler,
            presetSearchCategory,
            searchLimit,
            setActiveItemIndex,
            tracking,
            onResultItemClick,
            builderInfo,
        ],
    );

    const handleResultItemClick = useCallback(
        async (result: ISearchBarResult) => {
            if (onResultItemClick) {
                resetState();
                const query = await onResultItemClick(result);
                if (query && query.length > 0) {
                    setSearchQuery(query);
                }
            } else {
                if (result.linkType !== LinkType.NewTabLink) {
                    resetState();
                }

                if (
                    result.linkType === LinkType.WebformsLink &&
                    // eslint-disable-next-line no-restricted-syntax
                    !(await (window as any).JobPickerBridge.isJobInJobPicker(result.data.jobId))
                ) {
                    BTLocalStorage.set("bt-boolean-redirectedToJobNotInJobPicker", true);
                    // Reload the page if the url is correct, but the job isn't in the job picker since pages rely on the url changing to trigger updates
                    if (
                        result.link &&
                        result.link === window.location.pathname + window.location.hash
                    ) {
                        window.location.reload();
                    }
                }
            }
        },
        [resetState, onResultItemClick],
    );

    const handleSearchRef = useRef(handleSearch);
    useEffect(() => {
        handleSearchRef.current = handleSearch;
    }, [handleSearch]);

    const debounceHandleSearch = useMemo(
        () =>
            debounce(handleSearchRef.current, 300, {
                leading: false,
                trailing: true,
            }),
        [],
    );

    const handleInputChange = useCallback(
        (_: string, value: string) => {
            if (value.length === 0) {
                resetState(false);
                setIsListOpen(true);
                setLoading(false);
            } else {
                void debounceHandleSearch(value);
            }
            onValueChange?.(value);
            setSearchQuery(value);
        },
        [debounceHandleSearch, resetState, onValueChange],
    );

    const handleCloseSearchBar = useCallback(() => {
        resetState();
        setLoading(false);
        handleCancelPreviousApiRequest();
    }, [handleCancelPreviousApiRequest, resetState]);

    const handleOpenSearchBar = useCallback(async () => {
        if (!autoFocused.current && inputRef.current) {
            setIsListOpen(true);
            void debounceHandleSearch(inputRef.current.input.value);
        } else {
            autoFocused.current = false;
        }
    }, [debounceHandleSearch]);

    const handleOpenSearchIcon = useCallback(async () => {
        await handleOpenSearchBar();
        requestAnimationFrame(() => inputRef.current?.input.focus());
    }, [handleOpenSearchBar]);

    // #endregion

    // #region Mouse + Keyboard
    const [searchRef, searchKeysPressed] = useKeyPress<HTMLDivElement>([
        Key.ArrowUp,
        Key.ArrowDown,
        Key.Enter,
        Key.Escape,
    ]);

    const scrollToItem = useCallback((activeKeyboardItem: SearchInteractiveItems | string) => {
        if (
            typeof activeKeyboardItem === "string" &&
            activeKeyboardItem.includes(SearchInteractiveItems.SearchResults)
        ) {
            const activeRef = resultsRef.current?.querySelector(
                `#${activeKeyboardItem}`,
            )?.parentElement!;
            scrollToElement(activeRef, true);
        }
    }, []);

    const handleEnterPress = useCallback(
        async (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!activeKeyboardItem) {
                return;
            }
            e.preventDefault();

            if (activeKeyboardItem === SearchInteractiveItems.SearchInput) {
                void debounceHandleSearch(inputRef.current!.input.value);
            } else if (activeKeyboardItem.includes(SearchInteractiveItems.SearchResults)) {
                const result: ISearchBarResult | undefined =
                    searchData?.processedResults[getResultKeyIndex(activeKeyboardItem)];
                if (result) {
                    await handleResultItemClick(result);
                }
                if (shouldAutoFillOnHover) {
                    autoFocusSearchBar();
                }
                if (result?.link) {
                    history.push(match.url + result.link);
                }
            }
        },
        [
            activeKeyboardItem,
            debounceHandleSearch,
            handleResultItemClick,
            shouldAutoFillOnHover,
            autoFocusSearchBar,
            history,
            match.url,
            searchData,
        ],
    );

    const handleMouseDown = useCallback(
        (e: MouseEvent) => {
            // Refocus the main input if clicking anywhere but a nested BTSelect or result card
            const target = e.target as HTMLElement;
            if (
                !target?.matches(".SearchResults *") &&
                target?.id !== "searchBar" &&
                !target?.matches(".SearchBarResultsWithFooter  *")
            ) {
                resetState(false);
                setLoading(false);
                handleCancelPreviousApiRequest();
            }
        },
        [handleCancelPreviousApiRequest, resetState],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === Key.Tab || e.key === Key.Escape) {
                handleCloseSearchBar();
            }
        },
        [handleCloseSearchBar],
    );

    useEffect(() => {
        if (searchResults && searchResults.length > 0) {
            const order = [...searchResults.map((_, i) => getSearchResultKey(i))];
            setKeyboardOrder(order);
        } else {
            setKeyboardOrder([SearchInteractiveItems.SearchInput]);
        }
    }, [searchResults]);

    useEffect(() => {
        if (!isListOpen) {
            document.removeEventListener("mousedown", handleMouseDown);
        } else {
            document.addEventListener("mousedown", handleMouseDown);
        }

        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
        };
    }, [handleMouseDown, isListOpen]);

    useEffect(() => {
        const input = inputRef.current?.input;
        window.addEventListener("resize", handleCloseSearchBar);
        getCloseOnScrollTargets?.(input)?.forEach((el) =>
            el.addEventListener("scroll", handleCloseSearchBar),
        );

        return () => {
            window.removeEventListener("resize", handleCloseSearchBar);
            getCloseOnScrollTargets?.(input)?.forEach((el) =>
                el.removeEventListener("scroll", handleCloseSearchBar),
            );
        };
    }, [isListOpen, handleCloseSearchBar, getCloseOnScrollTargets]);

    // Whenever the active index gets reset to 0, scroll to it, if possible
    // Mainly for new searches
    useEffect(() => {
        if (activeItemIndex === 0) {
            scrollToItem(keyboardOrder[activeItemIndex]);
        }
    }, [keyboardOrder, activeItemIndex, scrollToItem]);

    useEffect(() => {
        if (keyboardOrder.length > 0) {
            if (searchKeysPressed[Key.ArrowDown]) {
                setActiveItemIndex((prevState) => {
                    const nextIndex = prevState < keyboardOrder.length - 1 ? prevState + 1 : 0;
                    scrollToItem(keyboardOrder[nextIndex]);
                    return nextIndex;
                });
            } else if (searchKeysPressed[Key.ArrowUp]) {
                setActiveItemIndex((prevState) => {
                    const nextIndex = prevState > 0 ? prevState - 1 : keyboardOrder.length - 1;
                    scrollToItem(keyboardOrder[nextIndex]);
                    return nextIndex;
                });
            }
        }
    }, [keyboardOrder, scrollToItem, searchKeysPressed, setActiveItemIndex]);

    useEffect(() => {
        document.addEventListener("mousemove", onHoverOutside);
        document.addEventListener("mouseup", onHoverOutside);
        return () => {
            document.removeEventListener("mousemove", onHoverOutside);
            document.removeEventListener("mouseup", onHoverOutside);
        };
    }, [onHoverOutside]);

    // #endregion

    const overlay = (
        <div className={cn("SearchResults", BTContentRoot)}>
            {screenToShow === SearchResultScreen.Error && <ErrorScreen errorState={errorState} />}
            {screenToShow === SearchResultScreen.Empty && (
                <EmptyScreen
                    searchTerm={searchQuery}
                    emptyScreenDisplay={props.emptyScreenDisplay}
                />
            )}
            {screenToShow === SearchResultScreen.Results && searchData!.processedResults && (
                <ResultsScreen
                    resultsRef={resultsRef}
                    searchData={searchData}
                    activeKeyboardItem={activeKeyboardItem}
                    handleResultItemClick={handleResultItemClick}
                    setActiveItemIndex={setActiveItemIndex}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
    return (
        <BTPopover
            placement="bottom"
            content={overlay}
            visible={screenToShow !== SearchResultScreen.Closed}
            overlayClassName={cn("SearchBarResultsWithFooter", { Open: isListOpen })}
            overlayStyle={{ width: searchRef.current?.offsetWidth }}
        >
            <div
                className={cn("SearchBar", {
                    Open: isListOpen,
                    Overlay: isCollapsed,
                    InputPadding: inputPadding,
                })}
            >
                {isCollapsed &&
                    (!isListOpen ? (
                        <BTButtonDeprecated
                            data-testid="search"
                            icon={<BTIconSearch size="large" className="SearchIcon" />}
                            onClick={handleOpenSearchIcon}
                        />
                    ) : (
                        <BTButtonDeprecated
                            data-testid="search-back"
                            icon={<BTIconArrowLeftOutlined className="SearchIcon" size="large" />}
                            onClick={handleCloseSearchBar}
                        />
                    ))}
                <div className="SearchInputContainer" ref={searchRef} onKeyDown={handleKeyDown}>
                    <AutoSearchInput
                        id="searchBar"
                        data-testid="searchBar"
                        className={cn("SearchBarInput", props.className, {
                            SearchBarInputOpen: isListOpen,
                            Hidden: isCollapsed && !isListOpen,
                        })}
                        value={props.value ?? searchQuery}
                        // An existing request should be cancelled when the user is typing
                        onChange={handleCancelPreviousApiRequest}
                        onChangeDebounced={handleInputChange}
                        onPressEnter={handleEnterPress}
                        onFocus={handleOpenSearchBar}
                        onBlur={noOpt}
                        fieldRef={inputRef}
                        placeholder={!shouldAutoFillOnHover ? "Search" : undefined}
                        prefix={!shouldAutoFillOnHover ? <BTIconSearch size={16} /> : undefined}
                        autoComplete="off"
                        disableFormSubmit
                        allowClear={!shouldAutoFillOnHover && !isLoading && searchQuery.length > 0}
                        suffix={suffix}
                    />
                </div>
            </div>
        </BTPopover>
    );
});

interface IErrorScreen {
    errorState?: string;
}

const ErrorScreen: React.FunctionComponent<IErrorScreen> = (props) => {
    return (
        <EmptyState image={<BTIconExclamationCircleOutlinedError size={70} />}>
            {props.errorState}
        </EmptyState>
    );
};

export interface IEmptyScreenProps {
    searchTerm?: string;
    emptyScreenDisplay?: (searchTerm?: string) => JSX.Element;
}

const EmptyScreen: React.FunctionComponent<IEmptyScreenProps> = ({
    emptyScreenDisplay,
    searchTerm,
}) => {
    return emptyScreenDisplay ? (
        emptyScreenDisplay(searchTerm)
    ) : (
        <EmptyState
            image={
                <BTIconMagnifyingGlassX
                    size={emptyStateIconSize}
                    className="EmptyStateIcon"
                    data-testid="EmptyStateIcon"
                />
            }
        >
            We didn't find anything matching your search.
        </EmptyState>
    );
};

interface IResultsScreen {
    resultsRef: MutableRefObject<HTMLDivElement | null>;
    searchData: SearchBarEntity | undefined;
    activeKeyboardItem: string;
    handleResultItemClick: (result: ISearchBarResult) => Promise<void>;
    setActiveItemIndex: Dispatch<SetStateAction<number>>;
    isLoading: boolean;
}

const ResultsScreen: React.FunctionComponent<IResultsScreen> = (props) => {
    return (
        <div ref={props.resultsRef}>
            {props.searchData!.processedResults.map((result, i) => (
                <SearchResultCard
                    index={i}
                    key={i}
                    active={props.activeKeyboardItem === getSearchResultKey(i)}
                    result={result}
                    onResultClick={props.handleResultItemClick}
                    onHover={props.setActiveItemIndex}
                />
            ))}
        </div>
    );
};
